import { useEffect, useState } from "react";
import { HandHelping, Heart, MessageCircle, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDateTime } from "@/lib/format";

export default function AdminPrayers() {
  const { t, lang } = useI18n();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const reload = async () => {
    try {
      const { data } = await api.get("/admin/prayers");
      setList(Array.isArray(data) ? data : []);
    } catch (_) {
      setList([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const onDeletePrayer = async (id) => {
    if (!window.confirm(t.admin.prayersPage.deleteConfirm)) return;
    try {
      await api.delete(`/admin/prayers/${id}`);
      toast.success(t.admin.prayersPage.deleted);
      reload();
    } catch (_) { toast.error(t.common.error); }
  };

  const onDeleteComment = async (prayerId, commentId) => {
    if (!window.confirm(t.admin.prayersPage.deleteCommentConfirm)) return;
    try {
      await api.delete(`/admin/prayers/${prayerId}/comments/${commentId}`);
      toast.success(t.admin.prayersPage.deleted);
      reload();
    } catch (_) { toast.error(t.common.error); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <HandHelping className="h-5 w-5 text-primary" />
        <h1 className="font-serif-display text-2xl font-semibold">{t.admin.prayersPage.title}</h1>
      </div>

      {loading ? (
        <Card className="rounded-2xl bg-white/60"><CardContent className="py-10 text-center text-sm text-muted-foreground">{t.common.loading}</CardContent></Card>
      ) : list.length === 0 ? (
        <Card className="rounded-2xl border-dashed bg-white/60">
          <CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="admin-prayers-empty">
            {t.admin.prayersPage.empty}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="admin-prayers-list">
          {list.map((p) => {
            const isOpen = !!expanded[p.id];
            const commentCount = (p.comments || []).length;
            return (
              <Card key={p.id} className="rounded-2xl ring-1 ring-black/5" data-testid={`admin-prayer-card-${p.id}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-serif-display text-base font-semibold">{p.author_name}</p>
                      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{fmtDateTime(p.created_at, lang)}</p>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <Badge variant="secondary" className="gap-1 text-[11px]" title="praying">
                        <Heart className="h-3 w-3 fill-current" />
                        {p.prayed_count || 0}
                      </Badge>
                      <Button size="sm" variant="outline" onClick={() => onDeletePrayer(p.id)} data-testid={`admin-prayer-delete-${p.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="whitespace-pre-wrap text-sm text-foreground/90">{p.body}</p>

                  <div className="flex items-center justify-between">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="gap-1 text-muted-foreground"
                      onClick={() => setExpanded((cur) => ({ ...cur, [p.id]: !cur[p.id] }))}
                      data-testid={`admin-prayer-toggle-${p.id}`}
                    >
                      <MessageCircle className="h-4 w-4" />
                      {(t.admin.prayersPage.commentsLabel || "{n} comments").replace("{n}", String(commentCount))}
                      {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                  </div>

                  {isOpen && commentCount > 0 && (
                    <div className="space-y-2">
                      <Separator />
                      {(p.comments || []).map((c) => (
                        <div key={c.id} className="flex items-start justify-between gap-2 rounded-xl bg-muted/50 p-2.5" data-testid={`admin-prayer-comment-${c.id}`}>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-xs font-semibold">{c.author_name}</p>
                              <p className="text-[10px] text-muted-foreground">{fmtDateTime(c.created_at, lang)}</p>
                            </div>
                            <p className="mt-1 whitespace-pre-wrap text-sm">{c.body}</p>
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => onDeleteComment(p.id, c.id)} data-testid={`admin-prayer-delete-comment-${c.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
